import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import {
  formatSalesApiError,
  getSalesArrayPayload,
  useSalesEndpoints,
} from '../hooks/useSalesApiResource';
import { getFieldValueLabel, normalizers } from './configurationAdminUtils';
import {
  normalizeStationPart,
  normalizeStationPartAssignment,
  normalizeStationPartCategory,
} from './stationPartsUtils';
import {
  filterPartAutoSelectCategories,
  getPartMatchRuleGroups,
  normalizePartMatchRule,
} from './partAutoSelectUtils';
import './ConfigurationAdmin.css';

const INITIAL_PAGE_SIZE = 25;

const compareParts = (left, right) => (
  left.sortOrder - right.sortOrder
  || left.partNumber.localeCompare(right.partNumber, undefined, { numeric: true })
  || left.name.localeCompare(right.name)
);

const buildAssignedPartCategoryRows = (categories, parts, assignments) => {
  const assignedPartIds = new Set(assignments.map((assignment) => assignment.partId));
  const partsByCategory = new Map();

  parts.forEach((part) => {
    if (!assignedPartIds.has(part.id)) return;
    const categoryParts = partsByCategory.get(part.categoryId) || [];
    categoryParts.push(part);
    partsByCategory.set(part.categoryId, categoryParts);
  });

  const rows = categories.map((category) => ({
    ...category,
    activeParts: [...(partsByCategory.get(category.id) || [])].sort(compareParts),
  }));
  const knownCategoryIds = new Set(categories.map((category) => category.id));
  const uncategorizedParts = parts
    .filter((part) => assignedPartIds.has(part.id) && !knownCategoryIds.has(part.categoryId))
    .sort(compareParts);

  if (uncategorizedParts.length > 0) {
    rows.push({
      id: 0,
      code: '',
      name: 'Uncategorized',
      description: 'Assigned parts whose category is unavailable',
      displayOrder: Number.MAX_SAFE_INTEGER,
      activeParts: uncategorizedParts,
    });
  }

  return rows
    .filter((category) => category.activeParts.length > 0)
    .sort((left, right) => (
      left.displayOrder - right.displayOrder
      || left.code.localeCompare(right.code, undefined, { numeric: true })
      || left.name.localeCompare(right.name)
    ));
};

const AutopickPartList = () => {
  const endpoints = useSalesEndpoints();
  const request = usePortalApiRequest();
  const [categories, setCategories] = useState([]);
  const [parts, setParts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [rules, setRules] = useState([]);
  const [stations, setStations] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldValues, setFieldValues] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // The Sales API can be sensitive to request bursts, so load this joined view sequentially.
      const stationResponse = await request({ method: 'GET', url: endpoints.stations.base });
      const nextStations = normalizers.stations(getSalesArrayPayload(stationResponse));
      setStations(nextStations);

      const nextAssignments = [];
      for (const station of nextStations) {
        const assignmentResponse = await request({
          method: 'GET',
          url: endpoints.partStations.base,
          params: { stationid: station.id },
        });
        nextAssignments.push(...getSalesArrayPayload(assignmentResponse)
          .map(normalizeStationPartAssignment)
          .filter((assignment) => assignment.stationId === station.id));
      }
      setAssignments([...new Map(nextAssignments
        .map((assignment) => [`${assignment.partId}:${assignment.stationId}`, assignment])).values()]);

      const partResponse = await request({ method: 'GET', url: endpoints.parts.base });
      setParts(getSalesArrayPayload(partResponse).map(normalizeStationPart));

      const categoryResponse = await request({ method: 'GET', url: endpoints.categories.base });
      setCategories(getSalesArrayPayload(categoryResponse).map(normalizeStationPartCategory));

      const ruleResponse = await request({ method: 'GET', url: endpoints.partMatchRules.base });
      setRules(getSalesArrayPayload(ruleResponse).map(normalizePartMatchRule));

      const fieldResponse = await request({ method: 'GET', url: endpoints.configFields.base });
      setFields(normalizers.fields(getSalesArrayPayload(fieldResponse)));

      const fieldValueResponse = await request({ method: 'GET', url: endpoints.configFieldValues.base });
      setFieldValues(normalizers.fieldValues(getSalesArrayPayload(fieldValueResponse)));
    } catch (requestError) {
      setError(formatSalesApiError(requestError, 'Unable to load Part Auto Pick data.'));
    } finally {
      setLoading(false);
    }
  }, [endpoints, request]);

  useEffect(() => {
    load();
  }, [load]);

  const categoryRows = useMemo(
    () => buildAssignedPartCategoryRows(categories, parts, assignments),
    [assignments, categories, parts]
  );
  const fieldById = useMemo(() => new Map(fields.map((field) => [field.id, field])), [fields]);
  const fieldValueById = useMemo(
    () => new Map(fieldValues.map((value) => [value.id, value])),
    [fieldValues]
  );
  const stationById = useMemo(
    () => new Map(stations.map((station) => [station.id, station])),
    [stations]
  );
  const assignmentStationIdsByPart = useMemo(() => {
    const stationIdsByPart = new Map();
    assignments.forEach((assignment) => {
      if (!assignment.partId || !assignment.stationId) return;
      const stationIds = stationIdsByPart.get(assignment.partId) || new Set();
      stationIds.add(assignment.stationId);
      stationIdsByPart.set(assignment.partId, stationIds);
    });
    return stationIdsByPart;
  }, [assignments]);
  const ruleStationIdsByPart = useMemo(() => {
    const stationIdsByPart = new Map();
    rules.forEach((rule) => {
      if (!rule.partId || !rule.stationId) return;
      const stationIds = stationIdsByPart.get(rule.partId) || new Set();
      stationIds.add(rule.stationId);
      stationIdsByPart.set(rule.partId, stationIds);
    });
    return stationIdsByPart;
  }, [rules]);
  const getPartStationIds = useCallback((partId) => {
    const stationIds = new Set([
      ...(assignmentStationIdsByPart.get(partId) || []),
      ...(ruleStationIdsByPart.get(partId) || []),
    ]);
    return [...stationIds].sort((leftId, rightId) => {
      const left = stationById.get(leftId);
      const right = stationById.get(rightId);
      return (left?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (right?.sortOrder ?? Number.MAX_SAFE_INTEGER)
        || (left?.stationName || `Station ${leftId}`).localeCompare(right?.stationName || `Station ${rightId}`)
        || leftId - rightId;
    });
  }, [assignmentStationIdsByPart, ruleStationIdsByPart, stationById]);

  const filteredCategoryRows = useMemo(
    () => filterPartAutoSelectCategories(categoryRows, selectedCategoryId, searchTerm),
    [categoryRows, searchTerm, selectedCategoryId]
  );
  const filteredParts = useMemo(
    () => filteredCategoryRows.flatMap((category) => category.activeParts),
    [filteredCategoryRows]
  );
  const displayedPartIds = useMemo(
    () => new Set(filteredParts.slice(0, pageSize).map((part) => part.id)),
    [filteredParts, pageSize]
  );
  const displayedCategoryRows = useMemo(() => filteredCategoryRows
    .map((category) => ({
      ...category,
      activeParts: category.activeParts.filter((part) => displayedPartIds.has(part.id)),
    }))
    .filter((category) => category.activeParts.length > 0), [displayedPartIds, filteredCategoryRows]);

  const assignedPartCount = useMemo(
    () => categoryRows.reduce((total, category) => total + category.activeParts.length, 0),
    [categoryRows]
  );
  const assignedPartIds = useMemo(
    () => new Set(categoryRows.flatMap((category) => category.activeParts.map((part) => part.id))),
    [categoryRows]
  );
  const assignedStationCount = useMemo(
    () => new Set(assignments.map((assignment) => assignment.stationId).filter(Boolean)).size,
    [assignments]
  );
  const ruleGroupCount = useMemo(() => new Set(rules
    .filter((rule) => assignedPartIds.has(rule.partId) && rule.stationId)
    .map((rule) => `${rule.partId}:${rule.stationId}:${rule.ruleGroupId}`)).size, [assignedPartIds, rules]);

  const conditionText = (rule) => {
    if (rule.requiredValueId) {
      const value = fieldValueById.get(rule.requiredValueId);
      const valueLabel = value ? getFieldValueLabel(value) : `Value ${rule.requiredValueId}`;
      return `${fieldById.get(value?.configFieldId)?.fieldName || 'Value'} is ${valueLabel}`;
    }
    if (!rule.fieldId) return 'Always selected (default)';
    const bounds = [
      rule.minValue !== null ? `at least ${rule.minValue}` : '',
      rule.maxValue !== null ? `at most ${rule.maxValue}` : '',
    ].filter(Boolean).join(' and ');
    return `${fieldById.get(rule.fieldId)?.fieldName || `Field ${rule.fieldId}`} is ${bounds || 'within range'}`;
  };

  const resetPageSize = () => setPageSize(INITIAL_PAGE_SIZE);

  return (
    <section className="sales-portal-page sales-autopick-rules-page" aria-labelledby="sales-autopick-parts-title">
      <Row className="mb-3">
        <Col><h4 id="sales-autopick-parts-title">Part Auto Pick</h4></Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="sales-autopick-summary" aria-label="Part Auto Pick summary">
        <span><strong>{assignedPartCount.toLocaleString()}</strong> assigned parts</span>
        <span><strong>{assignedStationCount.toLocaleString()}</strong> station types</span>
        <span><strong>{ruleGroupCount.toLocaleString()}</strong> rule groups</span>
      </div>

      <div className="config-admin-part-category-toolbar config-admin-auto-part-toolbar">
        <Form.Group>
          <Form.Label>Search parts or categories</Form.Label>
          <Form.Control
            type="search"
            placeholder="Part number, part name, category code, or category name"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              resetPageSize();
            }}
          />
          <Form.Text>
            {filteredParts.length.toLocaleString()} matching {filteredParts.length === 1 ? 'part' : 'parts'} in {filteredCategoryRows.length} {filteredCategoryRows.length === 1 ? 'category' : 'categories'}
          </Form.Text>
        </Form.Group>
        <Form.Group>
          <Form.Label>Category</Form.Label>
          <Form.Select
            value={selectedCategoryId}
            onChange={(event) => {
              setSelectedCategoryId(event.target.value);
              resetPageSize();
            }}
          >
            <option value="all">All assigned categories ({categoryRows.length})</option>
            {categoryRows.map((category) => (
              <option key={category.id || 'uncategorized'} value={category.id}>
                {category.code ? `${category.code} - ` : ''}{category.name} ({category.activeParts.length})
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {loading ? (
        <div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading Part Auto Pick rules...</div>
      ) : displayedCategoryRows.length === 0 ? (
        <div className="sales-no-data">No assigned parts match the current filters.</div>
      ) : (
        <div className="config-admin-auto-categories">
          {displayedCategoryRows.map((category) => (
            <Card key={category.id || 'uncategorized'} className="config-admin-auto-category">
              <Card.Header>
                <div>
                  <strong>{category.code ? `${category.code} - ` : ''}{category.name}</strong>
                  {category.description && category.description !== category.name && <small>{category.description}</small>}
                </div>
                <Badge bg="secondary">
                  {category.activeParts.length} {category.activeParts.length === 1 ? 'part' : 'parts'}
                </Badge>
              </Card.Header>
              <div className="table-responsive">
                <Table hover className="mb-0 sales-autopick-rule-table">
                  <thead><tr><th>Part</th><th>Station Auto Pick rules</th></tr></thead>
                  <tbody>
                    {category.activeParts.map((part) => (
                      <tr key={part.id}>
                        <td>
                          <strong>{part.partNumber || `Part ${part.id}`}</strong>
                          {part.name && <small>{part.name}</small>}
                        </td>
                        <td>
                          <div className="sales-autopick-rule-stations">
                            {getPartStationIds(part.id).map((stationId) => {
                              const station = stationById.get(stationId);
                              const groups = getPartMatchRuleGroups(rules, part.id, stationId);
                              return (
                                <div key={stationId} className="sales-autopick-station-rule">
                                  <div className="sales-autopick-station-rule-header">
                                    <strong>{station?.stationName || `Station ${stationId}`}</strong>
                                    <Button
                                      as={Link}
                                      size="sm"
                                      variant="outline-primary"
                                      to={`/sales/admin/configuration?station=${stationId}&view=part-auto-select`}
                                    >
                                      <i className="bi bi-pencil-square" aria-hidden="true"></i> Edit rules
                                    </Button>
                                  </div>
                                  {groups.length === 0 ? (
                                    <span className="text-muted">No automatic match rules</span>
                                  ) : (
                                    <div className="config-admin-auto-expression">
                                      {groups.map((group, groupIndex) => (
                                        <React.Fragment key={group.ruleGroupId}>
                                          {groupIndex > 0 && <span className="config-admin-rule-or">OR</span>}
                                          <span className="config-admin-auto-group">
                                            <Badge className="config-admin-auto-group-id" bg="success">Group {group.ruleGroupId}</Badge>
                                            <strong className="config-admin-auto-quantity">Qty {group.quantity}</strong>
                                            {group.conditions.map((rule, conditionIndex) => (
                                              <React.Fragment key={rule.id}>
                                                {conditionIndex > 0 && <em>AND</em>}
                                                <span>{conditionText(rule)}</span>
                                              </React.Fragment>
                                            ))}
                                          </span>
                                        </React.Fragment>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card>
          ))}
        </div>
      )}

      {pageSize < filteredParts.length && (
        <div className="text-center mt-3">
          <Button className="btn addbtnview mx-auto" onClick={() => setPageSize((current) => current + INITIAL_PAGE_SIZE)}>
            Load More
          </Button>
        </div>
      )}
    </section>
  );
};

export default AutopickPartList;
